/**
 * TrpcMembershipBillingReader — the production `MembershipBillingPort` adapter
 * over the typed tRPC client (041). Calls the live `member.billing.listInvoices`
 * query: the member's cookie session authenticates the same-origin request, and
 * `memberProcedure` on the API side guarantees only a member session reads it.
 *
 * Both `member` and `member.billing` are OPTIONAL on the published `AppRouter`
 * (the conditional-spread posture — `member.billing` is mounted only when the IS
 * client is configured). The adapter narrows before calling and fails loudly
 * (PortErrorBoundary catches it) rather than rendering silent/demo data — an
 * absent namespace is a composition/config error, not "no invoices" (P7).
 *
 * On a back-office `unavailable` outcome the reader THROWS: the fee view is
 * wrapped in `PortErrorBoundary(membershipBilling) -> Suspense` (Profile.tsx), so
 * the throw renders the honest `profile.fee.unavailable` state — never demo or
 * stale invoices (FR-005). Route-on-read: nothing is mirrored (P7).
 *
 * `getBillingFacts` + `requestEInvoiceProposal` (048) are REAL as of contract
 * 1.6.0. The facts ride the same `listInvoices` query the invoices come from —
 * one read, so the invoice list and the delivery method cannot be read at
 * different instants — and the request calls `member.billing.requestEInvoice`.
 * The retired `orderEInvoice` did nothing and reported success; it is gone.
 *
 * `initiatePayment` (044) calls the `member.billing.initiatePayment` mutation and
 * maps its discriminated result to a `PaymentResult`: `{ ok: true, redirectUrl }`
 * → `{ kind: 'redirect', redirectUrl }` (the browser full-page navigates to the
 * Paytrail hosted page); `{ ok: false, error }` → `{ kind: 'error', error }` (the
 * honest, non-disclosing arms — never a fabricated success, P7/FR-010).
 */

import { TRPCClientError } from '@trpc/client';

import type { WebTrpcClient } from '../trpc/client.js';
import type {
  BillingFacts,
  BillingMethod,
  EInvoiceEligibility,
  EInvoiceProposalRequest,
  EInvoiceRequestOutcome,
  EInvoiceRequestRefusal,
  Invoice,
  MembershipBillingPort,
  PaymentResult,
} from './MembershipBillingPort.js';

/** Thrown on a back-office `unavailable` outcome so PortErrorBoundary renders the honest state. */
export class MembershipBillingUnavailableError extends Error {
  constructor() {
    super('member.billing.listInvoices returned unavailable');
    this.name = 'MembershipBillingUnavailableError';
  }
}

type BillingNamespace = NonNullable<NonNullable<WebTrpcClient['member']>['billing']>;
type ListInvoicesOutput = Awaited<ReturnType<BillingNamespace['listInvoices']['query']>>;

// A Record, not a Set literal: a refusal arm added to the union without an
// entry here fails typecheck instead of silently rendering as 'unavailable'.
const KNOWN_REFUSAL_RECORD: Record<EInvoiceRequestRefusal, true> = {
  'already-requested': true,
  'retry-conflict': true,
  'unsupported-bank': true,
  refused: true,
  unavailable: true,
};
function isKnownRefusal(reason: string): reason is EInvoiceRequestRefusal {
  return Object.hasOwn(KNOWN_REFUSAL_RECORD, reason);
}

// Same fail-closed discipline for the READ side: an eligibility kind this build
// does not know (api/web skew mid-deploy) must degrade the e-invoice surface to
// `unavailable`, not fall into the can-request branch and crash the section.
const KNOWN_ELIGIBILITY_RECORD: Record<EInvoiceEligibility['kind'], true> = {
  'can-request': true,
  pending: true,
  'already-einvoice': true,
  'method-unknown': true,
  unavailable: true,
};

/** Every bank pickable and distinct: integer id, non-empty name, no duplicate id. */
function isWellFormedBankList(banks: unknown): boolean {
  if (!Array.isArray(banks)) return false;
  const ids = new Set<number>();
  for (const bank of banks as Array<{ id?: unknown; name?: unknown } | null>) {
    if (bank === null || typeof bank !== 'object') return false;
    if (!Number.isInteger(bank.id) || typeof bank.name !== 'string' || bank.name.trim() === '') {
      return false;
    }
    if (ids.has(bank.id as number)) return false;
    ids.add(bank.id as number);
  }
  return true;
}

/** A strict `YYYY-MM-DD` that round-trips as a real calendar day. */
function isCalendarDate(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
  if (match === null) return false;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

function knownBillingFacts(raw: {
  billingMethod?: unknown;
  eligibility?: unknown;
  overdueNotice?: unknown;
}): BillingFacts {
  // 057: only the exact 'show' shows the notice. A value this build does not
  // know, or an older api that sends none, must never tell a member they owe.
  const overdueNotice = raw.overdueNotice === 'show' ? 'show' : 'hide';
  return { ...knownEInvoiceFacts(raw), overdueNotice };
}

function knownEInvoiceFacts(raw: {
  billingMethod?: unknown;
  eligibility?: unknown;
}): Omit<BillingFacts, 'overdueNotice'> {
  const billingMethod: BillingMethod =
    raw.billingMethod === 'paper' || raw.billingMethod === 'einvoice'
      ? raw.billingMethod
      : 'unknown';
  const e = raw.eligibility as
    | { kind?: unknown; banks?: unknown; requestDate?: unknown; canRequestAgainOn?: unknown }
    | null
    | undefined;
  const kind = typeof e?.kind === 'string' ? e.kind : '';
  const unavailable = { billingMethod, eligibility: { kind: 'unavailable' } } as const;
  if (!Object.hasOwn(KNOWN_ELIGIBILITY_RECORD, kind)) return unavailable;
  // The server offers the switch (or reports a pending proposal) only for a
  // member on exactly `paper`. Under skew a missing/unknown method must not
  // still offer it — the harm `method-unknown` exists to prevent (FR-001a).
  if ((kind === 'can-request' || kind === 'pending') && billingMethod !== 'paper') {
    return { billingMethod, eligibility: { kind: 'method-unknown' } };
  }
  if (kind === 'can-request' && !isWellFormedBankList(e?.banks)) return unavailable;
  if (
    kind === 'pending' &&
    !(isCalendarDate(e?.requestDate) && isCalendarDate(e?.canRequestAgainOn))
  ) {
    return unavailable;
  }
  return { billingMethod, eligibility: e as EInvoiceEligibility };
}

/**
 * Pass the server's outcome through, but fail closed on anything this build
 * does not know (e.g. api/web version skew mid-deploy): an unknown status or
 * refusal reason renders as the honest transient refusal, never a blank or raw
 * message key.
 */
function knownOutcome(typed: EInvoiceRequestOutcome): EInvoiceRequestOutcome {
  // The static type is a promise about the api; this is a runtime check of it.
  const outcome = typed as { readonly status: string; readonly reason?: string };
  if (outcome.status === 'ok' || outcome.status === 'unconfirmed') {
    return { status: outcome.status };
  }
  if (
    outcome.status === 'refused' &&
    outcome.reason !== undefined &&
    isKnownRefusal(outcome.reason)
  ) {
    return { status: 'refused', reason: outcome.reason };
  }
  return { status: 'refused', reason: 'unavailable' };
}

export class TrpcMembershipBillingReader implements MembershipBillingPort {
  readonly #client: WebTrpcClient;
  // One in-flight `listInvoices` query shared by `listInvoices()` and
  // `getBillingFacts()`: the surface asks for both in the same render, and they
  // must come from ONE server read (one IS call, one instant). Cleared on
  // settle, so every later read (a refetch) issues a fresh query.
  #inflight: Promise<ListInvoicesOutput> | null = null;

  constructor(client: WebTrpcClient) {
    this.#client = client;
  }

  #listInvoicesQuery(): Promise<ListInvoicesOutput> {
    if (this.#inflight === null) {
      const query = this.#billing()
        .listInvoices.query()
        .finally(() => {
          if (this.#inflight === query) this.#inflight = null;
        });
      this.#inflight = query;
    }
    return this.#inflight;
  }

  #billing() {
    const member = this.#client.member;
    if (member === undefined) {
      throw new Error(
        'TrpcMembershipBillingReader: the member namespace is absent on the AppRouter — ' +
          'the invoice surface requires the 041 API activation.',
      );
    }
    const billing = member.billing;
    if (billing === undefined) {
      throw new Error(
        'TrpcMembershipBillingReader: member.billing is absent on the AppRouter — the invoice ' +
          'surface requires the IntegrationService client to be configured (041).',
      );
    }
    return billing;
  }

  async listInvoices(): Promise<Invoice[]> {
    const result = await this.#listInvoicesQuery();
    if (result.status !== 'ok') {
      throw new MembershipBillingUnavailableError();
    }
    // Map the wire invoice → the web Invoice. A payment detail the server
    // reports as null is omitted, not set to undefined. The billing method is
    // an envelope-level fact, read via getBillingFacts (048).
    return result.invoices.map((inv) => ({
      id: inv.id,
      number: inv.number,
      amount: inv.amount,
      dueDate: inv.dueDate,
      status: inv.status,
      paymentReference: inv.paymentReference,
      membershipYear: inv.membershipYear,
      payable: inv.payable,
      ...(inv.invoiceDate !== null ? { invoiceDate: inv.invoiceDate } : {}),
      ...(inv.accountNumber !== null ? { accountNumber: inv.accountNumber } : {}),
      ...(inv.bank !== null ? { bank: inv.bank } : {}),
      // 044 US3: the overlay display label (authoritative-paid / optimistic-paid
      // / unpaid / unavailable). Present when the api's payment store is wired.
      ...(inv.paidLabel !== undefined ? { paidLabel: inv.paidLabel } : {}),
    }));
  }

  async getBillingFacts(): Promise<BillingFacts> {
    // The SAME in-flight query listInvoices uses — the facts ride that envelope,
    // so no second round-trip and no chance of the two disagreeing.
    const result = await this.#listInvoicesQuery();
    if (result.status !== 'ok') {
      throw new MembershipBillingUnavailableError();
    }
    return knownBillingFacts(result);
  }

  async requestEInvoiceProposal(
    request: EInvoiceProposalRequest,
    idempotencyKey: string,
  ): Promise<EInvoiceRequestOutcome> {
    // The server owns the decision: it re-reads, re-derives eligibility with its
    // own clock, re-checks live membership, and binds the key to the member
    // before calling the register. This is a pass-through, deliberately — any
    // "help" here would be a second derivation that could disagree with the one
    // that actually enforces.
    //
    // A REJECTED mutation (transport failure) is not a success and not a throw
    // the caller must remember to catch: it is the honest transient refusal
    // (contract §4), so every port consumer gets a value.
    // Resolved OUTSIDE the try: an absent billing namespace is a composition
    // error that must fail loudly (class doc), not read as "try again shortly".
    const billing = this.#billing();
    // A read already in flight was issued BEFORE this write: the re-read that
    // follows the attempt must not reuse it, or it would show pre-write state.
    this.#inflight = null;
    try {
      const outcome = await billing.requestEInvoice.mutate({
        bankId: request.bankId,
        idempotencyKey,
      });
      this.#inflight = null;
      // Assigning the router-inferred output to the web union keeps the compile
      // guard: an arm the api adds that the web lacks fails typecheck here.
      const typed: EInvoiceRequestOutcome = outcome;
      return knownOutcome(typed);
    } catch (error) {
      this.#inflight = null;
      // An expired session is NOT a transient refusal (owner decision
      // 2026-09-28). `memberProcedure` rejects before the mutation body runs, so
      // nothing reached the register and no retry can succeed until the member
      // signs in again — "try again shortly" would send them in a loop.
      //
      // Discriminated on `data.code`, and only for a genuine `TRPCClientError`:
      // a non-tRPC error that happens to carry a `data.code` by structural
      // coincidence must not be read as an auth failure (the TrpcContentReader
      // precedent).
      if (error instanceof TRPCClientError && error.data?.code === 'UNAUTHORIZED') {
        return { status: 'session-expired' };
      }
      return { status: 'refused', reason: 'unavailable' };
    }
  }

  async initiatePayment(invoiceId: string): Promise<PaymentResult> {
    // 044 direct-Paytrail: call the member-authed mutation and map its
    // discriminated union to a PaymentResult. The mutation never throws for the
    // typed error arms — it returns `{ ok: false, error }` — so no arm is
    // coerced to a fabricated success (P7/FR-010).
    const result = await this.#billing().initiatePayment.mutate({ invoiceId });
    return result.ok
      ? { kind: 'redirect', redirectUrl: result.redirectUrl }
      : { kind: 'error', error: result.error };
  }
}
